import { useEffect, useRef, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { RepeatableList } from '@/components/onboarding/RepeatableList';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { CharCounter } from '@/components/shared/CharCounter';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { KbProfileFormValues } from '@/hooks/useKbProfileForm';
import { CAPS, PRICE_TYPES } from '@/hooks/useKbProfileForm';
import { prefersReducedMotion } from '@/lib/formFocus';
import type { CatalogItem, PriceType } from '@/services/kbProfileService';
import { FieldHelp } from '@/components/onboarding/FieldHelp';

interface CatalogStepProps {
  form: UseFormReturn<KbProfileFormValues>;
  /** When true (offers === ['products_digital'] only), the duration field is
   * hidden — duration is meaningless for digital products. */
  isDigitalOnly?: boolean;
}

interface CatalogRowProps {
  item: CatalogItem;
  index: number;
  update: (next: CatalogItem) => void;
  isDigitalOnly: boolean;
}

/** Fields validated on a catalog item, in visual order — used both to build
 * the field id (`catalog-{idx}-{field}`) and to pick the first invalid one for
 * the F149 auto-focus-on-error behavior. */
const CATALOG_FIELD_ORDER = ['name', 'price', 'duration', 'lead_time', 'description'] as const;

function FieldLabel({ htmlFor, children }: { htmlFor: string; children: React.ReactNode }) {
  return (
    <label className="text-sm font-medium" htmlFor={htmlFor}>
      {children}
    </label>
  );
}

/** F149 — collapsed-card summary: name + the price pair (mirrors the Review
 * step's pairing of name + price-or-price-type). Rendered both as the
 * collapsed row's content and as the expanded card's header (so the toggle's
 * accessible name always includes the item's name). */
function CatalogSummary({ item }: { item: CatalogItem }) {
  const { t } = useTranslation();
  const name = item.name?.trim() || t('onboarding.kbForm.catalog.item_untitled');
  const priceText = item.price?.trim()
    ? item.price
    : t(`onboarding.kbForm.price_type_${item.price_type ?? 'quote'}`);

  return (
    <span className="flex min-w-0 flex-1 items-center gap-2">
      <span className="min-w-0 truncate text-sm font-medium">{name}</span>
      <span className="shrink-0 text-xs text-muted-foreground">{priceText}</span>
    </span>
  );
}

function CatalogRow({ item, index, update, isDigitalOnly }: CatalogRowProps) {
  const { t } = useTranslation();
  const fieldId = (name: string) => `catalog-${index}-${name}`;
  // F069 #8 — single price affordance: the type select leads; "sob consulta"
  // carries no number, so the free price input only renders for priced types.
  const showPriceInput = (item.price_type ?? 'quote') !== 'quote';

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div className="space-y-1">
        <FieldLabel htmlFor={fieldId('name')}>
          {t('onboarding.kbForm.catalog.name_label')}
        </FieldLabel>
        <Input
          id={fieldId('name')}
          className="h-11"
          placeholder={t('onboarding.kbForm.catalog.name_placeholder')}
          value={item.name}
          maxLength={CAPS.catalog_name}
          onChange={(e) => update({ ...item, name: e.target.value })}
        />
      </div>
      <div className="space-y-1">
        <FieldLabel htmlFor={fieldId('price-type')}>
          {t('onboarding.kbForm.catalog.price_type_label')}
        </FieldLabel>
        <Select
          value={item.price_type ?? 'quote'}
          onValueChange={(val) => update({ ...item, price_type: val as PriceType })}
        >
          <SelectTrigger id={fieldId('price-type')} className="h-11">
            <SelectValue placeholder={t('onboarding.kbForm.catalog.price_type_label')} />
          </SelectTrigger>
          <SelectContent>
            {PRICE_TYPES.map((pt) => (
              <SelectItem key={pt} value={pt}>
                {t(`onboarding.kbForm.price_type_${pt}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {showPriceInput && (
        <div className="space-y-1 sm:col-span-2">
          <FieldLabel htmlFor={fieldId('price')}>
            {t('onboarding.kbForm.catalog.price_label')}
          </FieldLabel>
          <Input
            id={fieldId('price')}
            className="h-11"
            placeholder={t('onboarding.kbForm.catalog.price_placeholder')}
            value={item.price ?? ''}
            maxLength={CAPS.catalog_price}
            onChange={(e) => update({ ...item, price: e.target.value })}
          />
          <p className="text-xs text-muted-foreground">{t('onboarding.kbForm.step3.price_hint')}</p>
        </div>
      )}
      {!isDigitalOnly && (
        <div className="space-y-1">
          <FieldLabel htmlFor={fieldId('duration')}>
            {t('onboarding.kbForm.catalog.duration_label')}
          </FieldLabel>
          <Input
            id={fieldId('duration')}
            className="h-11"
            placeholder={t('onboarding.kbForm.catalog.duration_placeholder')}
            value={item.duration ?? ''}
            maxLength={CAPS.catalog_duration}
            onChange={(e) => update({ ...item, duration: e.target.value })}
          />
        </div>
      )}
      <div className={`space-y-1${isDigitalOnly ? ' sm:col-span-2' : ''}`}>
        <FieldLabel htmlFor={fieldId('lead-time')}>
          {t('onboarding.kbForm.catalog.lead_time_label')}
        </FieldLabel>
        <Input
          id={fieldId('lead-time')}
          className="h-11"
          placeholder={t('onboarding.kbForm.catalog.lead_time_placeholder')}
          value={item.lead_time ?? ''}
          maxLength={CAPS.catalog_lead_time}
          onChange={(e) => update({ ...item, lead_time: e.target.value })}
        />
      </div>
      <div className="space-y-1 sm:col-span-2">
        <FieldLabel htmlFor={fieldId('description')}>
          {t('onboarding.kbForm.catalog.description_label')}
        </FieldLabel>
        <Textarea
          id={fieldId('description')}
          rows={2}
          className="resize-none"
          placeholder={t('onboarding.kbForm.catalog.description_placeholder')}
          value={item.description ?? ''}
          maxLength={CAPS.catalog_description}
          onChange={(e) => update({ ...item, description: e.target.value })}
        />
        <CharCounter value={item.description ?? ''} max={CAPS.catalog_description} />
      </div>
    </div>
  );
}

export function CatalogStep({ form, isDigitalOnly = false }: CatalogStepProps) {
  const { t } = useTranslation();
  const catalog = form.watch('catalog') ?? [];

  // F149 — accordion: at most one item's full form is open at a time.
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  // Deferred DOM actions that must wait for the target row to actually be in
  // the document (it only mounts once `expandedIndex` catches up in a
  // subsequent render). Applied by the effect below, which — deliberately
  // undeclared deps — re-runs after every commit until its target appears.
  const pendingScrollAddRef = useRef<number | null>(null);
  const pendingFocusRef = useRef<{ idx: number; field: string } | null>(null);

  useEffect(() => {
    if (pendingScrollAddRef.current != null) {
      const idx = pendingScrollAddRef.current;
      const el = document.getElementById(`catalog-${idx}-name`);
      if (el) {
        el.scrollIntoView({
          block: 'nearest',
          behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        });
        pendingScrollAddRef.current = null;
      }
    }

    if (pendingFocusRef.current) {
      const { idx, field } = pendingFocusRef.current;
      const el = document.getElementById(`catalog-${idx}-${field}`);
      if (el) {
        el.scrollIntoView({
          block: 'center',
          behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        });
        el.focus({ preventScroll: true });
        pendingFocusRef.current = null;
      }
    }
  });

  // F149 — validation auto-expand: a catalog item's error must never hide
  // silently behind a collapsed summary. Re-fires whenever `errors` gets a
  // new identity (a fresh validation round, e.g. KbSection's handleNext ->
  // form.trigger(['catalog'])) — including a round that leaves the same item
  // invalid, so the field is refocused each time the owner retries.
  const { errors } = useFormState({ control: form.control });
  // RHF's `formState` is Proxy-backed for its read-tracking optimization —
  // in practice the `errors` object reference is NOT guaranteed to change
  // between renders even when its content did (observed: same reference
  // across a render where `errors.catalog` went from absent to populated).
  // A `useEffect([errors])` dependency would silently never re-fire, so this
  // is keyed on a content signature instead (no dependency array — runs
  // every render, guarded manually) per the ticket's "errors object identity
  // or trigger count" note.
  const lastCatalogErrorSignatureRef = useRef<string | null>(null);
  // Deliberately no deps array (see comment above); the signature guard
  // above prevents any update loop.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const catalogErrors = errors.catalog as
      | Array<
          Partial<Record<(typeof CATALOG_FIELD_ORDER)[number], { message?: string }>> | undefined
        >
      | undefined;
    const signature = catalogErrors ? JSON.stringify(catalogErrors) : null;
    if (signature === lastCatalogErrorSignatureRef.current) return;
    lastCatalogErrorSignatureRef.current = signature;

    if (!catalogErrors) return;

    const idx = catalogErrors.findIndex((e) => Boolean(e));
    if (idx === -1) return;

    setExpandedIndex(idx);

    const field = CATALOG_FIELD_ORDER.find((f) => Boolean(catalogErrors[idx]?.[f]));
    if (field) {
      pendingFocusRef.current = { idx, field };
    }
  });

  function handleCatalogChange(next: CatalogItem[]) {
    const prev = catalog;
    form.setValue('catalog', next, { shouldValidate: true });

    if (next.length > prev.length) {
      // RepeatableList's append always pushes to the end.
      const newIdx = next.length - 1;
      setExpandedIndex(newIdx);
      pendingScrollAddRef.current = newIdx;
      return;
    }

    if (next.length < prev.length) {
      // Find which position was removed by locating the first divergence —
      // `remove(idx)` keeps every other item's reference, only shifting
      // positions past idx down by one.
      let removedIdx = prev.length - 1;
      for (let i = 0; i < next.length; i++) {
        if (prev[i] !== next[i]) {
          removedIdx = i;
          break;
        }
      }
      setExpandedIndex((cur) => {
        if (cur == null) return cur;
        if (cur === removedIdx) return null;
        if (cur > removedIdx) return cur - 1;
        return cur;
      });
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center gap-1">
          <p className="text-sm font-medium">{t('onboarding.kbForm.step3.catalog_label')}</p>
          <FieldHelp baseKey="onboarding.kbForm.step3.catalog" />
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          {t('onboarding.kbForm.step3.catalog_hint')}
        </p>
      </div>
      <RepeatableList
        items={catalog}
        onChange={handleCatalogChange}
        emptyItem={() => ({
          name: '',
          price: '',
          price_type: 'quote' as const,
          duration: '',
          lead_time: '',
          description: '',
        })}
        maxEntries={CAPS.catalog_items}
        testId="catalog-list"
        addLabelKey="onboarding.kbForm.catalog.add_item"
        expandedIndex={expandedIndex}
        onExpandedChange={setExpandedIndex}
        renderSummary={(item) => <CatalogSummary item={item} />}
        renderRow={(item, idx, update) => (
          <CatalogRow item={item} index={idx} update={update} isDigitalOnly={isDigitalOnly} />
        )}
      />
    </div>
  );
}
